import {
  TypeHolidayBundlesCardFields,
  TypeProductFields,
} from '@/core/generated-types';
import { extractIdFromShopifyStr } from '@/utils/helpers';
import { Entry } from 'contentful';

import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import { ITEM_ORIGINS } from '@/core/constants';
import { ShopifyProductType } from '@/lib/shopify';
import { CartLineInput } from '@/lib/shopify';

interface ExtendedProductFields extends TypeProductFields {
  shopifyProduct?: ShopifyProduct;
}

interface BundleData {
  name: string;
  playKitsQuantity: number;
  includeBookBundles: boolean;
}

interface AnalyticsCardData {
  currency: string;
  name: string;
  totalPrice: number;
  discount: number;
  items: Entry<ExtendedProductFields>[];
  quantity: number;
  source: string;
  selectedVariant: ShopifyVariant;
  playKitsQuantity: number;
  includeBookBundles: boolean;
}

export const getBundleAvailability = (
  includes: TypeHolidayBundlesCardFields['includes'],
) => {
  return includes
    .filter((item) => item.sys.contentType.sys.id === 'product') // Filter only products
    .some((product) => {
      const variants = product.fields.shopifyProduct?.variants;
      if (variants && variants.length === 1) {
        // Single variant case
        return !variants[0].availableForSale;
      } else {
        // Multiple variants case
        return variants
          ? variants.every(
              (variant: ShopifyVariant) => !variant.availableForSale,
            )
          : null;
      }
    });
};

export const generateCartItems = (
  includes: TypeHolidayBundlesCardFields['includes'],
  quantity: number,
): CartLineInput[] => {
  return includes
    .filter(
      (item: any) =>
        item.sys.contentType.sys.id === 'product' &&
        !item.fields.isPlayKit &&
        !item.fields.shopifyProduct?.tags.includes('the-play-kitchen'),
    )
    .map((item: any) => {
      const isSkillSet =
        item.fields.shopifyProduct?.productType ===
        ShopifyProductType.SKILL_SET;
      const isCoursePack =
        item.fields.shopifyProduct?.productType === ShopifyProductType.COURSE;
      return {
        merchandiseId: item.fields.shopifyProduct.variants[0].id,
        quantity,
        attributes: [
          { key: 'item_origin', value: ITEM_ORIGINS.PRODUCT_PAGE },
          {
            key: 'quantity_limited_to_one',
            value: String(isCoursePack || isSkillSet),
          },
          { key: 'is_course_pack', value: String(isCoursePack) },
          { key: 'is_skill_set', value: String(isSkillSet) },
        ],
      };
    });
};

export const generatePlayKitchenItem = (
  selectedVariant: ShopifyVariant,
  quantity: number,
) => ({
  merchandiseId: selectedVariant?.id || '',
  quantity,
  attributes: [
    { key: 'item_origin', value: ITEM_ORIGINS.PRODUCT_PAGE },
    { key: 'quantity_limited_to_one', value: 'false' },
  ],
});

export type NavigationRouter = {
  push: (url: string) => void; // Include any other methods you need
};

export const redirectAfterAddToCart = async (
  router: NavigationRouter,
  includePlayKits: TypeHolidayBundlesCardFields['includePlayKits'],
  includeBookBundles: TypeHolidayBundlesCardFields['includeBookBundles'],
  lang: string,
) => {
  const euLocales = ['nl', 'fr', 'de', 'es'];
  if (includePlayKits) {
    router.push(
      `/pages/subscription-v3/child-info?simplifiedSubflow=true&size=${includePlayKits}&addOns=${includeBookBundles}`,
    );
  } else {
    const isEULocale = euLocales.includes(lang);
    window.location.href = isEULocale ? `/${lang}/cart` : '/cart';
  }
};

export const generateNameId = (name: string) => {
  return name.trim().replace(/,/g, '').replace(/\s+/g, '_').toLowerCase();
};

// This is a static data for the play kits by age range
// including the price and book bundle price for each region
const playKitsByAgeRange = {
  ['0-12-months']: [
    { region: 'us', price: 80, bookBundle: 18 },
    { region: 'uk', price: 80, bookBundle: 19 },
    { region: 'eu', price: 84, bookBundle: 20 },
    { region: 'au', price: 130 },
  ],
  ['1-3-years']: [
    { region: 'us', price: 120, bookBundle: 18 },
    { region: 'uk', price: 120, bookBundle: 19 },
    { region: 'eu', price: 126, bookBundle: 20 },
    { region: 'au', price: 195 },
  ],
  ['3+-years']: [
    { region: 'us', price: 120, bookBundle: 18 },
    { region: 'uk', price: 120, bookBundle: 19 },
    { region: 'eu', price: 126, bookBundle: 20 },
    { region: 'au', price: 195 },
  ],
};

const calculateBundlePrice = (
  ageRange: '0-12-months' | '1-3-years' | '3+-years',
  quantity: number,
  includeBookBundles: boolean,
) => {
  const region = process.env.NEXT_PUBLIC_REGION?.toLocaleLowerCase();

  const playKitAgeRange = playKitsByAgeRange[ageRange] ?? [];
  const playKit = playKitAgeRange.find((kit) => kit.region === region);

  const playKitPrice = playKit?.price || 0;
  const bookBundlePrice = includeBookBundles ? playKit?.bookBundle || 0 : 0;

  return (playKitPrice + bookBundlePrice) * quantity;
};

export const generateBundleData = ({
  name,
  playKitsQuantity,
  includeBookBundles,
}: BundleData) => {
  // This reuses the markup added for useTrackPlayKitPLPProducts
  // to get some data available from the parent component
  const bundleCard = document.querySelector(
    `[data-ga-section="product-card"][data-ga-name="${name}"]`,
  ) as HTMLElement;

  const explorerSections = document.querySelectorAll(
    `[data-ga-section="explorer-section"]`,
  );
  const sectionIndex = [...explorerSections].indexOf(
    bundleCard?.parentElement?.parentElement as HTMLElement,
  );

  const listId = sectionIndex !== -1 ? `year${sectionIndex}` : '';
  const listName = bundleCard.dataset.gaListName?.trim() || '';

  const bundleProductType = playKitsQuantity
    ? 'Play Kit Bundle'
    : 'Playthings Bundle';
  const bundleAgeRange = bundleCard.dataset.listName?.trim() || '';
  const bundleAgeRangeId = bundleCard.dataset.listId?.trim() as
    | '0-12-months'
    | '1-3-years'
    | '3+-years';

  const bundlePrice = calculateBundlePrice(
    bundleAgeRangeId,
    playKitsQuantity,
    includeBookBundles,
  );

  return {
    name,
    sku: generateNameId(name),
    price: bundlePrice,
    productType: bundleProductType,
    category: 'BFCM Bundle',
    ageRange: bundleAgeRange,
    variantTitle: 'Bundles',
    listId,
    listName,
  };
};

export const generateAnalyticsCartData = ({
  currency,
  name,
  totalPrice,
  discount,
  items,
  quantity,
  source,
  selectedVariant,
  playKitsQuantity,
  includeBookBundles,
}: AnalyticsCardData) => {
  const discountApplied = Number((1 - discount).toFixed(2));
  const discountPercentage = `${discountApplied * 100}% discount`;

  const bundleData = generateBundleData({
    name,
    playKitsQuantity,
    includeBookBundles,
  });

  const bundle = {
    product_title: name,
    sku: bundleData.sku,
    price: bundleData.price,
    product_type: bundleData.productType,
    item_category2: bundleData.category,
    item_category3: bundleData.ageRange,
    item_category4: discountPercentage,
    item_category5: source,
    variant_title: bundleData.variantTitle,
    list_id: bundleData.listId,
    list_name: bundleData.listName,
    discount: Number((bundleData.price * discountApplied).toFixed(2)),
    quantity,
  };

  const bundleItems = items.map((item: Entry<ExtendedProductFields>) => {
    const shopifyProduct = item.fields?.shopifyProduct;
    const variant = selectedVariant || shopifyProduct?.variants[0];

    return {
      product_title: item.fields.title || '',
      sku: variant?.sku || '',
      price: variant?.price?.amount,
      product_type: shopifyProduct?.productType || '',
      item_category2: bundleData.category,
      item_category3: bundleData.ageRange,
      item_category4: discountPercentage,
      item_category5: source,
      variant_title: 'Bundles',
      list_id: bundleData.listId,
      list_name: bundleData.listName,
      product_id: extractIdFromShopifyStr(shopifyProduct?.id || ''),
      variant_id: extractIdFromShopifyStr(variant?.id || ''),
      discount: Number(
        ((variant?.price?.amount || 0) * discountApplied).toFixed(2),
      ),
      quantity,
    };
  });

  const cartItems = [bundle, ...bundleItems];
  const isStandaloneProduct = items.length === 1 && !playKitsQuantity;
  // Remove bundle kit from standalone items such as Play Kitchen or RSS
  if (isStandaloneProduct) {
    cartItems.splice(0, 1);
  }

  return {
    currency,
    total_price: totalPrice,
    items: cartItems,
  };
};
